// sheet-focus.ts — the game's dialogs keep the keyboard (SC-60137). The vendored sheet (Last rounds, How to play,
// Provably fair, Settings, Autobet) is a non-modal <dialog> over a scrim: the scrim stops the pointer, but focus stayed
// on the button that opened it and Tab walked the board behind — a keyboard or screen-reader user could reach and change
// the live bet (stake, win chance, mode) under an open dialog, with no visible focus.
//
// While the sheet is on:
//   - the board behind it is `inert` — every child of #frame EXCEPT the scrim, which must keep taking the click that
//     closes the sheet, and the game's live region (#feedLive: nothing in it can take focus, and an autobet run that
//     ends or is refused while a dialog is open must still be announced). Inert content is out of the tab order and
//     out of the accessibility tree, so focus cannot leave the dialog for the page (no focus-trap loop needed);
//   - focus moves into the dialog (the dialog itself: a screen reader announces its title);
// and when it closes, the board is released and focus returns to the control that opened it.
// The sheet's own state is its `is-on` class (the game adds it on open and removes it the moment a close starts), so
// this only observes — the vendored game is not changed. Only what this module made inert is released: a notice that
// froze the whole page (boot.ts) stays as it is.
const MARK = "data-dice-sheet-inert";

export function keepFocusInSheet(doc: Document): void {
  const sheet = doc.getElementById("sheet");
  const frame = doc.getElementById("frame");
  if (!sheet || !frame) return;
  let on = false;
  let opener: HTMLElement | null = null;

  const sync = (): void => {
    const now = sheet.classList.contains("is-on");
    if (now === on) return;
    on = now;
    if (on) {
      const active = doc.activeElement;
      opener = active instanceof HTMLElement && active !== doc.body && !sheet.contains(active) ? active : null;
      for (const child of Array.from(frame.children)) {
        if (child.id === "scrim" || child.hasAttribute("aria-live") || child.hasAttribute("inert")) continue;
        child.setAttribute("inert", "");
        child.setAttribute(MARK, "");
      }
      if (!sheet.hasAttribute("tabindex")) sheet.setAttribute("tabindex", "-1");
      sheet.focus({ preventScroll: true });
      return;
    }
    for (const child of Array.from(frame.querySelectorAll(`:scope > [${MARK}]`))) {
      child.removeAttribute("inert");
      child.removeAttribute(MARK);
    }
    const active = doc.activeElement;
    const adrift = active === null || active === doc.body || sheet.contains(active);
    if (opener && opener.isConnected && adrift) opener.focus({ preventScroll: true });
    opener = null;
  };

  new MutationObserver(sync).observe(sheet, { attributes: true, attributeFilter: ["class"] });
  sync();
}
